import type { GpuBudgetCatalog } from '../types/models/GpuBudget';
import type { ApiResponseGpuBudgetCatalog } from '../types/api/ApiResponseGpuBudget';
import type { GpuJsendEnvelope } from './gpuServerResponse';
import { useGpuBudgetFetchOnce } from './useGpuBudgetFetchOnce';

export interface UseGpuBudgetCatalogReturn {
  /** `null` until the first successful read. */
  catalog: GpuBudgetCatalog | null;
  loading: boolean;
  error: string | null;
  /** Background re-read (Refresh): the current catalog stays on screen. */
  refetch: () => Promise<void>;
}

const pickCatalog = (body: GpuJsendEnvelope): GpuBudgetCatalog | null => {
  const catalog = (body as ApiResponseGpuBudgetCatalog).data?.catalog;
  return catalog && Array.isArray(catalog.kinds) && typeof catalog.limits === 'object' && catalog.limits !== null
    ? catalog
    : null;
};

/**
 * `GET /datacenters/gpu-servers/budgets/catalog` — what a budget can hold for this organisation: the
 * kinds (in section order) with their bounds, units and products at this organisation's prices, plus
 * the limits (docs/api-types/gpu-budgets.md). The editor is driven by it: no kind, bound, unit or
 * limit is ever a frontend constant. Read once when enabled, then only on `refetch` (Refresh).
 */
export const useGpuBudgetCatalog = (enabled: boolean = true): UseGpuBudgetCatalogReturn => {
  const { data, loading, error, refetch } = useGpuBudgetFetchOnce(
    '/datacenters/gpu-servers/budgets/catalog',
    pickCatalog,
    'Could not load the budget catalog.',
    enabled,
  );
  return { catalog: data, loading, error, refetch };
};

export default useGpuBudgetCatalog;
